import ChevronIcon from "@mui/icons-material/ChevronRight";
import DeleteIcon from "@mui/icons-material/DeleteOutlineOutlined";
import EditIcon from "@mui/icons-material/Edit";
import FileDownloadIcon from "@mui/icons-material/FileDownloadOutlined";
import {
  Box,
  Button,
  IconButton,
  Link,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { ReactNode, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import { useCase, useCaseSubmit } from "../context/CaseContext";
import { useCurrency } from "../context/CurrencyContext";
import { useTheme } from "../context/ThemeContext.tsx";
import { usePersona } from "../context/usePersona.ts";
import {
  CaseUserRef,
  Claim,
  ClaimKind,
  ClaimTypes,
  Party,
} from "../shared/CaseModel.ts";
import {
  computeSettlementStatus,
  getValueOfAssets,
  isPartySettled,
} from "../shared/CaseModelUtility.ts";
import {
  attributionName,
  getAmountClaimedAgainstParties,
  getAmountClaimedByParties,
} from "./CaseOverviewUtils.ts";
import { CaseTitleDialog } from "./CaseTitleDialog.tsx";
import DeleteCaseDialog from "./DeleteCaseDialog.tsx";
import { ExportCaseDialog } from "./ExportCaseDialog.tsx";
import { Metric } from "./general/DataAtoms.tsx";
import {
  casePartyReserveOf,
  ReserveCard,
  ReserveSheet,
} from "./reserve/ReserveSheet.tsx";
import { signedTone } from "./general/dataAtoms/shared.ts";
import { SectionLabel } from "./general/SectionLabel.tsx";
import { TonalBadge } from "./general/TonalBadge.tsx";
import ClaimTypeChip from "./issues/ClaimTypeChip.tsx";
import { PartyOverview, PartyOverviewCard } from "./PartyOverviewCard.tsx";
import { judgmentStatusOf } from "../shared/judgment";

type ClaimRoleSummary = {
  claimedTypes: string[];
  defendedTypes: string[];
  otherTypes: string[];
};
export const CaseOverview = () => {
  const caseContext = useCase();
  const c = caseContext.getCase();
  const {
    id,
    name,
    createdAt,
    updatedAt,
    createdBy,
    updatedBy,
    claims,
    parties,
    agreements,
    results,
    settlements,
    costStructure,
  } = c;
  const { t } = useTranslation(undefined, { keyPrefix: "CaseOverview" });
  const { active: persona } = usePersona();
  // for the names other pages give to themselves
  const { t: tRoot } = useTranslation();

  /**
   * A timestamp badge, naming who saved it when that is known. With no usable
   * name the date-only label is used, so an unattributed case reads exactly as it
   * did before attribution was recorded — never "by undefined".
   */
  const stampLabel = (
    kind: "created" | "updated",
    date: string,
    by?: CaseUserRef,
  ) => {
    const stamp = new Date(date).toLocaleString();
    const name = attributionName(by);
    return name
      ? t(`${kind}AtBy`, { date: stamp, name })
      : t(`${kind}At`, { date: stamp });
  };

  function summarizeClaimsByRole(
    claims: Claim[],
    clients: Party[],
  ): ClaimRoleSummary {
    const clientIds = new Set(clients.map(({ id }) => id));

    const claimedTypes: string[] = [];
    const defendedTypes: string[] = [];
    const otherTypes: string[] = [];

    claims.forEach(({ type = ClaimTypes.Other, claimant, defendants }) => {
      if (clientIds.has(claimant.id)) {
        claimedTypes.push(type);
        return;
      }

      if (defendants?.some(({ id }) => clientIds.has(id))) {
        defendedTypes.push(type);
        return;
      }

      otherTypes.push(type);
    });

    return { claimedTypes, defendedTypes, otherTypes };
  }

  function countClaimTypes(types: string[]): { [type: string]: number } {
    const countPerType: { [type: string]: number } = {};

    for (const type of types)
      countPerType[type] = (countPerType[type] ?? 0) + 1;

    return countPerType;
  }

  function extractOverviewData({
    id,
    name,
    client,
    assets,
  }: Party): PartyOverview {
    const stats = results?.partyStats?.find(
      (stat) => stat.partyId === id,
    )?.stats;
    const { estimatedTotal, incurredTotal } = caseContext.getCostSummaryOf(id);
    const partyAgreements = agreements?.find((a) => a.partyId === id);
    const settled = isPartySettled(
      claims ?? [],
      parties ?? [],
      id,
      settlements,
    );
    return {
      id,
      name,
      client,
      assetLimit: getValueOfAssets({ assets }),
      costs: { incurred: incurredTotal, estimated: estimatedTotal },
      winChance: stats ? 1 - stats.probabilityOfNegativeJudgement : undefined,
      netResult: stats?.netResult.average,
      settled,
      agreements: {
        lawFirmCfa: partyAgreements?.lawFirmCfa?.active ?? false,
        barristerCfa: partyAgreements?.barristerCfa?.active ?? false,
      },
    };
  }

  const clients = parties?.filter(({ client }) => client) ?? [];
  const { claimedTypes, defendedTypes, otherTypes } = summarizeClaimsByRole(
    claims ?? [],
    clients,
  );

  const claimedTypeCounts = useMemo(
    () => Object.entries(countClaimTypes(claimedTypes)),
    [claimedTypes],
  );

  const defendedTypeCounts = useMemo(
    () => Object.entries(countClaimTypes(defendedTypes)),
    [defendedTypes],
  );

  const otherTypeCounts = useMemo(
    () => Object.entries(countClaimTypes(otherTypes)),
    [otherTypes],
  );

  const totalClaimedAgainstClients = useMemo(
    () =>
      getAmountClaimedAgainstParties(
        claims ?? [],
        clients.map((c) => c.id),
      ),
    [claims, clients],
  );
  const totalClaimedByClients = useMemo(
    () =>
      getAmountClaimedByParties(
        claims ?? [],
        clients.map((c) => c.id),
      ),
    [claims, clients],
  );

  const judgmentStatus = judgmentStatusOf(caseContext.getCase());
  const settlementStatus = useMemo(
    () =>
      computeSettlementStatus(claims ?? [], parties ?? [], settlements ?? []),
    [claims, parties, settlements],
  );

  const opponents = useMemo(() => {
    const opponentIds = new Set<string>();
    const claimMatrix = caseContext.getClaimMatrix();
    clients.forEach(({ id }) =>
      Object.keys(claimMatrix.getOpponentsOf(id)).forEach((party) =>
        opponentIds.add(party),
      ),
    );
    return (
      parties?.filter(({ id, client }) => !client && opponentIds.has(id)) ?? []
    );
  }, [caseContext]);

  const otherParties = useMemo(
    () =>
      parties?.filter(
        ({ id, client }) =>
          !client && !opponents.some((opponent) => opponent.id === id),
      ) ?? [],
    [caseContext],
  );

  const submit = useCaseSubmit();
  const setTitle = (title: string, claimKind?: ClaimKind): Promise<void> =>
    Promise.resolve(
      submit(claimKind ? { title, claimKind } : { title }, { method: "put" }),
    );

  const { currency } = useCurrency();
  const { theme } = useTheme();
  const [showTitleDialog, setShowTitleDialog] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);

  // the firm's profit needs the fee structure that splits its fees
  const feeStructureEntered = costStructure?.clientFeeStructure !== undefined;
  const profitIncurred = results?.revenue?.total.incurred.margin ?? 0;

  const card = { borderRadius: 3, px: 2.5, pt: 2, pb: 2.25 } as const;
  const buttonSx = { textTransform: "none", fontWeight: 600 } as const;

  /** The heading of a card, with the way to the page its content comes from. */
  const cardHeader = (label: string, link: string, href: string) => (
    <Stack
      direction="row"
      spacing={1.5}
      sx={{ alignItems: "center", justifyContent: "space-between" }}
    >
      <SectionLabel component="h2">{label}</SectionLabel>
      <Link
        component={RouterLink}
        to={href}
        variant="body2"
        underline="hover"
        sx={{ display: "inline-flex", alignItems: "center", fontWeight: 600 }}
      >
        {link}
        <ChevronIcon fontSize="small" />
      </Link>
    </Stack>
  );

  /**
   * The claims the clients have one role in: their types and, where they are
   * claimed by or against the clients, what they come to.
   */
  const claimsInRole = (
    label: string,
    types: [string, number][],
    amount?: number,
  ) => (
    <Box sx={{ minWidth: 0 }}>
      <Typography
        variant="caption"
        sx={{ display: "block", fontWeight: 600, color: "text.secondary" }}
      >
        {label}
      </Typography>
      <Stack
        direction="row"
        spacing={0.5}
        useFlexGap
        sx={{ flexWrap: "wrap", mt: 0.75 }}
      >
        {types.map(([type, count]) => (
          <ClaimTypeChip
            key={type}
            type={type}
            label={t("claimTypeWithCount", {
              count,
              type: t(`claimTypes.${type}`),
            })}
          />
        ))}
      </Stack>
      {amount !== undefined && (
        <Metric
          label={t("claims.totalAmountsClaimed")}
          value={currency.format(amount)}
          sx={{ mt: 1.5 }}
        />
      )}
    </Box>
  );

  /**
   * A group of parties under its name: the clients one below the other, any
   * other group side by side.
   */
  const partySection = (
    label: string,
    group: Party[],
    sideBySide: boolean,
  ): ReactNode => (
    <Stack component="section" spacing={1.25}>
      <SectionLabel component="h2" rule>
        {label}
      </SectionLabel>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: sideBySide
            ? `repeat(auto-fit, minmax(${theme.spacing(42)}, 1fr))`
            : "1fr",
          gap: 2.25,
        }}
      >
        {group.map((party) => (
          <PartyOverviewCard key={party.id} {...extractOverviewData(party)} />
        ))}
      </Box>
    </Stack>
  );

  return (
    <Stack spacing={2.5} sx={{ mb: 2 }}>
      {/* The case */}
      <Stack
        direction="row"
        spacing={2}
        useFlexGap
        sx={{
          alignItems: "flex-end",
          justifyContent: "space-between",
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
            <Typography
              variant="h5"
              component="h1"
              sx={{ fontWeight: 700, letterSpacing: "-.01em" }}
            >
              {name}
            </Typography>
            {setTitle !== undefined && (
              <Tooltip title={t("changeTitle")}>
                {/* the tooltip needs an element that fires events, which a
                    disabled button does not */}
                <span>
                  <IconButton
                    size="small"
                    aria-label={t("changeTitle")}
                    disabled={caseContext.readOnly}
                    onClick={() => setShowTitleDialog(!showTitleDialog)}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            {judgmentStatus !== "none" && (
              <TonalBadge tone={theme.palette.primary} sx={{ ml: 0.5 }}>
                {t(`judgment.${judgmentStatus}`, { keyPrefix: "CasesList" })}
              </TonalBadge>
            )}
            {judgmentStatus === "none" && settlementStatus === "full" && (
              <TonalBadge
                tone={theme.palette.data.positive}
                title={t("settlement.fullyCaption")}
                sx={{ ml: 0.5 }}
              >
                {t("settlement.badge.full", { keyPrefix: "CasesList" })}
              </TonalBadge>
            )}
          </Stack>
          <Stack
            direction="row"
            useFlexGap
            sx={{
              flexWrap: "wrap",
              gap: theme.spacing(0.25, 2.25),
              mt: 0.5,
              color: "text.secondary",
            }}
          >
            {persona === "insurer" && (
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {tRoot(
                  `case.claimKind.${caseContext.getCase().claimKind ?? "thirdPartyLiability"}`,
                )}
              </Typography>
            )}
            <Typography variant="body2">
              {t("caseId", { caseId: id })}
            </Typography>
            {createdAt && (
              <Typography variant="body2">
                {stampLabel("created", createdAt, createdBy)}
              </Typography>
            )}
            {updatedAt && (
              <Typography variant="body2">
                {stampLabel("updated", updatedAt, updatedBy)}
              </Typography>
            )}
          </Stack>
        </Box>
        <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap" }}>
          <Button
            variant="outlined"
            startIcon={<FileDownloadIcon />}
            onClick={() => setExportDialogOpen(true)}
            sx={buttonSx}
          >
            {t("export")}
          </Button>
          <Button
            color="error"
            startIcon={<DeleteIcon />}
            disabled={caseContext.readOnly}
            onClick={() => setDeleteDialogOpen(true)}
            sx={buttonSx}
          >
            {t("delete")}
          </Button>
        </Stack>
      </Stack>

      {/* What is claimed, and what the firm makes */}
      <Stack
        direction="row"
        spacing={2.25}
        useFlexGap
        sx={{ flexWrap: "wrap" }}
      >
        <Paper
          variant="outlined"
          sx={{ ...card, flex: `2 1 ${theme.spacing(52)}`, minWidth: 0 }}
        >
          {cardHeader(
            tRoot("case.claims.title"),
            t("shortcuts.claims.title", { count: claims?.length ?? 0 }),
            "configuration/claims",
          )}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: `repeat(auto-fit, minmax(${theme.spacing(26)}, 1fr))`,
              gap: theme.spacing(2, 3.5),
              mt: 1.75,
            }}
          >
            {claimedTypes.length > 0 &&
              claimsInRole(
                t("claims.claimed", { count: clients.length }),
                claimedTypeCounts,
                totalClaimedByClients,
              )}
            {defendedTypes.length > 0 &&
              claimsInRole(
                t("claims.defended", { count: clients.length }),
                defendedTypeCounts,
                totalClaimedAgainstClients,
              )}
            {otherTypes.length > 0 &&
              claimsInRole(t("claims.other"), otherTypeCounts)}
          </Box>
        </Paper>

        {/* what the claim costs the insurer: the reserve of its insured */}
        {persona === "insurer" && clients.length > 0 && (
          <Box sx={{ flex: `1 1 ${theme.spacing(32)}`, minWidth: 0 }}>
            <ReserveCard
              reserve={casePartyReserveOf(caseContext.getCase(), clients[0].id)}
            />
          </Box>
        )}
        {/* the firm's profit, the law firm's alone */}
        {persona === "lawFirm" && (
          <Paper
            variant="outlined"
            sx={{ ...card, flex: `1 1 ${theme.spacing(32)}`, minWidth: 0 }}
          >
            {cardHeader(
              t("headers.lawFirm"),
              tRoot("case.details.section.analytics.revenue"),
              "analytics/revenue",
            )}
            {feeStructureEntered ? (
              <Metric
                label={t("shortcuts.profit")}
                value={currency.format(profitIncurred)}
                caption={t("profitCaption")}
                tone={signedTone(profitIncurred)}
                sx={{ mt: 1.75 }}
              />
            ) : (
              <Metric
                label={t("shortcuts.profit")}
                value={t("feeStructureMissing")}
                sx={{ mt: 1.75 }}
              />
            )}
          </Paper>
        )}
      </Stack>

      {persona === "insurer" &&
        clients.map(({ id, name: partyName }) => {
          const reserve = casePartyReserveOf(caseContext.getCase(), id);
          return (
            reserve && (
              <ReserveSheet
                key={id}
                reserve={reserve}
                name={clients.length > 1 ? partyName : undefined}
              />
            )
          );
        })}

      {partySection(
        t("headers.parties.clients", { count: clients.length }),
        clients,
        false,
      )}
      {opponents.length > 0 &&
        partySection(
          t("headers.parties.opponents", { count: opponents.length }),
          opponents,
          true,
        )}
      {otherParties.length > 0 &&
        partySection(
          t("headers.parties.other", { count: otherParties.length }),
          otherParties,
          true,
        )}

      <CaseTitleDialog
        initialTitle={name}
        // an insurer says what kind of claim it is too
        initialClaimKind={
          persona === "insurer"
            ? (caseContext.getCase().claimKind ?? "thirdPartyLiability")
            : undefined
        }
        onClose={() => setShowTitleDialog(false)}
        saveTitle={setTitle}
        open={showTitleDialog}
      />
      <DeleteCaseDialog
        caseId={id}
        caseTitle={name}
        deleteAction={`/cases/${id}/delete`}
        onClose={() => setDeleteDialogOpen(false)}
        open={deleteDialogOpen}
      />
      <ExportCaseDialog
        caseToExport={c}
        onClose={() => setExportDialogOpen(false)}
        open={exportDialogOpen}
      />
    </Stack>
  );
};
